import { Grid, TextField, Typography, List, ListItem, ListItemText, Box, IconButton, Chip } from "@mui/material";
import { memo, useState, useCallback, useRef } from "react";
import { SecondaryBackgroundButton } from "../components/Buttons.js";
import { generateGrammar } from "../api/index.js";
import { useSnackbar } from "../utils/index.js";

import CodeMirror from "@uiw/react-codemirror";
import { yaml } from "@codemirror/lang-yaml";
import { EditorView, lineNumbers } from "@codemirror/view";
import { lintGutter } from "@codemirror/lint";

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import Spinner from "../components/Spinner.js";

const GrammarGen = () => {
	const [newRequirement, setNewRequirement] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [requirements, setRequirements] = useState([]);
	const [editingIndex, setEditingIndex] = useState(null);
	const [editedText, setEditedText] = useState("");
	const [generatedGrammar, setGeneratedGrammar] = useState(
		"# No grammar generated yet. Add requirements and click 'Generate Grammar'."
	);
	const [isGrammarValid, setIsGrammarValid] = useState(false);

	const { success, error } = useSnackbar();

	const codeMirrorRef = useRef(null);
	const [loading, setLoading] = useState(false);

	const handleGenerateGrammar = async () => {
		setLoading(true);
		setIsGrammarValid(false);
		setErrorMessage("");
		console.log("Generating grammar from:", requirements);
		if (requirements.length === 0) {
			setGeneratedGrammar("No requirements provided.");
			setLoading(false);
			return;
		}

		let augmentedRequirements = [];
		for (const req of requirements) {
			augmentedRequirements.push(`R${requirements.indexOf(req) + 1} - The grammar should ${req}\n`);
		}

		const concatenatedRequirements = augmentedRequirements.join();

		await generateGrammar(concatenatedRequirements)
			.then((response) => {
				if (response.success) {
					success("Grammar generated successfully.");
					setIsGrammarValid(true);
					setGeneratedGrammar(response.grammar);
					setErrorMessage("");
				} else {
					error("An error occurred while generating grammar.");
					setIsGrammarValid(false);
					setGeneratedGrammar(response.grammar || "Error generating grammar.");
					setErrorMessage(response.error || "An unknown error occurred.");
				}
			})
			.catch((error) => {
				error("Error generating grammar.");
				setIsGrammarValid(false);
				setGeneratedGrammar("Error generating grammar.");
				setErrorMessage("Network error or service unavailable.");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDownloadGrammar = () => {
		const blob = new Blob([generatedGrammar], { type: "text/plain" });

		const url = window.URL.createObjectURL(blob);

		const link = document.createElement("a");
		link.href = url;
		link.download = "grammar.tx";
		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);

		success("Grammar downloaded successfully.");
	};

	const addRequirement = (e) => {
		e.preventDefault();
		if (newRequirement.trim() === "") return;
		setRequirements([...requirements, newRequirement]);
		setNewRequirement("");
	};

	const onGrammarChange = useCallback((value) => {
		setGeneratedGrammar(value);
	}, []);

	const handleEditClick = (index, text) => {
		setEditingIndex(index);
		setEditedText(text);
	};

	const handleSaveEdit = (index) => {
		const updated = [...requirements];
		updated[index] = editedText;
		setRequirements(updated);
		setEditingIndex(null);
	};

	const handleCancelEdit = () => {
		setEditingIndex(null);
		setEditedText("");
	};

	const hasGeneratedGrammar = generatedGrammar && generatedGrammar !== "# No grammar generated yet. Add requirements and click 'Generate Grammar'.";

	return (
		<Grid container spacing={4} sx={{ p: 1, display: "flex", height: "100%", justifyContent: "center" }}>
			<Spinner open={loading} />
			<Grid item xs={12} md={6} py={2}>
				<Typography variant="h5" sx={{ color: "white.main", mb: 2 }}>
					Requirements
				</Typography>

				<form
					style={{ display: "flex", gap: "12px", width: "100%", marginBottom: "12px" }}
					onSubmit={addRequirement}
				>
					<TextField
						fullWidth
						label="The grammar should"
						placeholder="e.g., support basic arithmetic operations"
						variant="filled"
						sx={{ backgroundColor: "background.paper", borderRadius: 1 }}
						value={newRequirement}
						onChange={(e) => setNewRequirement(e.target.value)}
					/>
					<SecondaryBackgroundButton
						title="Add"
						titleColor="primary.main"
						type="submit"
					/>
				</form>

				<List
					sx={{
						borderRadius: 1,
						maxHeight: "450px",
						overflowY: "auto",
						bgcolor: "background.paper",
						mb: "12px",
						py: 0,
					}}
				>
					{requirements.length === 0 ? (
						<Typography variant="body2" sx={{ p: 2, color: "text.secondary" }}>
							No requirements added yet.
						</Typography>
					) : (
						requirements.map((req, index) => (
							<ListItem
								key={index}
								sx={{
									borderBottom: "1px solid",
									borderColor: "divider",
									"&:last-child": { borderBottom: "none" },
									py: 0.5,
									px: 2,
									backgroundColor:
										editingIndex === index ? "secondary.main" : "inherit",
								}}
							>
								{editingIndex === index ? (
									<>
										<TextField
											fullWidth
											value={editedText}
											onChange={(e) => setEditedText(e.target.value)}
											size="small"
											variant="outlined"
											autoFocus
											onKeyDown={(e) => {
												if (e.key === "Enter") handleSaveEdit(index);
												if (e.key === "Escape") handleCancelEdit();
											}}
										/>
										<IconButton color="success.main" onClick={() => handleSaveEdit(index)}>
											<CheckIcon />
										</IconButton>
										<IconButton color="inherit" onClick={handleCancelEdit}>
											<CloseIcon />
										</IconButton>
									</>
								) : (
									<>
										<ListItemText primary={`${index + 1}. ${req}`} />
										<IconButton
											onClick={() => handleEditClick(index, req)}
										>
											<EditIcon color="primary.main" />
										</IconButton>
										<IconButton
											onClick={() =>
												setRequirements(requirements.filter((_, i) => i !== index))
											}
										>
											<DeleteIcon color="error" />
										</IconButton>
									</>
								)}
							</ListItem>
						))
					)}
				</List>

				<SecondaryBackgroundButton
					width="100%"
					title="Generate Grammar"
					titleColor="primary.main"
					disabled={loading}
					onClick={() => handleGenerateGrammar()}
				/>
			</Grid>

			<Grid item xs={12} md={6} py={2} sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 100px)" }}>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
					<Typography variant="h5" sx={{ color: "white.main" }}>
						Generated Grammar
					</Typography>
					<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
						{hasGeneratedGrammar && (
							<Chip
								icon={isGrammarValid ? <CheckCircleIcon /> : <ErrorIcon />}
								label={isGrammarValid ? "Valid Grammar" : "Invalid Grammar"}
								color={isGrammarValid ? "success" : "error"}
								variant="outlined"
								sx={{ fontWeight: "bold" }}
							/>
						)}
						{hasGeneratedGrammar && (
							<SecondaryBackgroundButton
								title="Download"
								titleColor="primary.main"
								startIcon={<DownloadIcon />}
								onClick={handleDownloadGrammar}
							/>
						)}
					</Box>
				</Box>
				<Box
					sx={{
						border: "1px solid",
						borderColor: isGrammarValid ? "success.main" : (errorMessage ? "error.main" : "divider"),
						borderRadius: 1,
						bgcolor: "background.paper",
						overflowY: "auto",
						flex: 1,
					}}
				>
					<CodeMirror
						ref={codeMirrorRef}
						value={generatedGrammar}
						onChange={onGrammarChange}
						theme="light"
						height="100%"
						extensions={[
							yaml(),
							lineNumbers(),
							EditorView.lineWrapping,
							lintGutter(),
							EditorView.editable.of(true),
						]}
					/>
				</Box>
				{errorMessage && (
					<Box
						sx={{
							border: "1px solid",
							borderColor: "error.main",
							borderRadius: 1,
							maxHeight: "150px",
							bgcolor: "background.paper",
							overflowY: "auto",
							padding: 2,
							marginTop: 2,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
							<ErrorIcon color="error" />
							<Typography variant="subtitle1" sx={{ color: "error.main", fontWeight: "bold" }}>
								Validation Error
							</Typography>
						</Box>
						<Typography variant="body2" sx={{ color: "text.secondary", fontFamily: "monospace" }}>
							{errorMessage}
						</Typography>
					</Box>
				)}
			</Grid>
		</Grid>
	);
};

export default memo(GrammarGen);